/**
 * The Charter Board's pure model (M5 plan C17, spec §15.2, §18 flows 4 and 7): one row per system (region, holder
 * with colour chip and mark, holder S, top challenger S, lead, 10-day sparklines of the top two, state icons,
 * tariff and toll, contested flag, flip countdown, the Ops/Sec/Inf bars), sort and filter, the header aggregates
 * (claims per faction, active wars, gate status, a pending vote) and the Votes panel. Tested headless; the React
 * components only render it. Colour is never the only carrier: every chip has a mark, every state a glyph and a
 * word, the contested flag a hatch glyph and the word.
 *
 * The player corporation (`'player'`, D-424 D5, spec §27.7) shows under its own name and livery colour with a `★` mark:
 * the claim bar counts its claims, its rows show the security band and the tariff ("not set" when automatic), it has
 * a score row, and the Votes panel names its ballot and notes that a ban binds its space (§27.8).
 *
 * UI events only (built when the Board opens or its data arrives).
 */
import { z } from 'zod'
import factionsJson from '../../data/factions.json'
import type { RegionId } from '../../galaxy/types'
import { gateStatusText } from '../../strategic/wire/m5'
import { bandLabel, CORP_CLAIM_LABELS, corpClaimName, fillLabel } from '../../features/charterHud/hudModel'
import {
  FACTION_IDS,
  PLAYER_FACTION_ID,
  type CharterVote,
  type ClaimCorpView,
  type ClaimView,
  type CorpFactionId,
  type FactionId,
  type GalaxyEconomyView,
  type GateView,
  type RelationsView,
  type StateKind,
} from '../../strategic/types'
import boardJson from './board.json'

export const boardConfigSchema = z.object({
  comment: z.string().optional(),
  refreshS: z.number().positive(),
  sparkline: z.object({
    width: z.number().positive(),
    height: z.number().positive(),
    sMax: z.number().positive(),
  }),
  overlays: z.object({
    heldSize: z.number().positive(),
    hollowSize: z.number().positive(),
    warSize: z.number().positive(),
    frontWidth: z.number().positive(),
    tollMax: z.number().positive(),
    tollMinSize: z.number().positive(),
    tollMaxSize: z.number().positive(),
  }),
  corp: z.object({
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    markPrefix: z.string().min(1),
    scope: z.string().min(1),
    lawTooltip: z.string().min(1),
  }),
  /** Intel › Charter in the dashboard (W3 card M1). */
  dashboard: z.object({
    label: z.string().min(1),
    order: z.number(),
  }),
})
export type BoardConfig = z.infer<typeof boardConfigSchema>

export function loadBoardConfig(raw: unknown = boardJson): BoardConfig {
  return boardConfigSchema.parse(raw)
}

// ---------------------------------------------------------------------------------------------
// Faction looks, regions, states
// ---------------------------------------------------------------------------------------------

export interface FactionLook {
  name: string
  /** Two-letter mark (the colour-blind-safe carrier). */
  mark: string
  color: string
}

const LOOKS: ReadonlyMap<string, FactionLook> = new Map(
  Object.entries(factionsJson.factions).map(
    ([id, f]) =>
      [
        id,
        {
          name: f.short,
          mark: f.short.slice(0, 2).toUpperCase(),
          color: f.mapColor,
        },
      ] as const,
  ),
)
const UNKNOWN_COLOR = '#6B7380'

/** The player corporation as the Board and the overlays show it (from the save or the corporation view). */
export interface BoardCorp {
  name: string
  /** The livery's primary colour. */
  color: string
}

let boardCorpCfg: BoardConfig['corp'] | null = null
function corpCfg(): BoardConfig['corp'] {
  boardCorpCfg ??= loadBoardConfig().corp
  return boardCorpCfg
}

/** The corporation's look: its name, `★` and its initial as the mark, its livery colour (fallbacks from data). */
export function corpLook(corp: BoardCorp | null | undefined): FactionLook {
  const name = corpClaimName(corp)
  return {
    name,
    mark: `${corpCfg().markPrefix}${name.slice(0, 1).toUpperCase()}`,
    color: corp?.color ?? corpCfg().color,
  }
}

/** The look of any claim-view id: the corporation's for `'player'`, else the faction's. */
export function holderLook(id: CorpFactionId, corp: BoardCorp | null | undefined): FactionLook {
  return id === PLAYER_FACTION_ID ? corpLook(corp) : factionLook(id)
}

/** "Binds ‹Corp› space: its stations and held systems" (a ban applies there too: spec §27.8). */
export function corpScopeText(corp: BoardCorp | null | undefined): string {
  return fillLabel(corpCfg().scope, { name: corpClaimName(corp) })
}

/** The Law overlay's tooltip line for a corporation claim (no jurisdiction: crimes book to the victim's faction). */
export function corpLawTooltip(): string {
  return corpCfg().lawTooltip
}

/** A corporation row's tariff cell: "4%" for a manual tariff, "not set" for an automatic one (spec §27.6). */
export function corpTariffCell(tariffSet: boolean, tariff: number): string {
  return tariffSet ? `${Math.round(tariff * 100)}%` : CORP_CLAIM_LABELS.notSet
}

/** The faction's short name, mark and map colour (unknown ids: the id, its first letters and grey). */
export function factionLook(id: string): FactionLook {
  return (
    LOOKS.get(id) ?? {
      name: id,
      mark: id.slice(0, 2).toUpperCase(),
      color: UNKNOWN_COLOR,
    }
  )
}

export const REGION_LABELS: Readonly<Record<RegionId, string>> = {
  gate: 'Gate Cluster',
  mid: 'Mid Reach',
  deep: 'Deep Reach',
  rim: 'Rim',
}
export const REGION_ORDER: readonly RegionId[] = ['gate', 'mid', 'deep', 'rim']

export const STATE_GLYPHS: Readonly<Record<StateKind, string>> = {
  boom: '▲',
  bust: '▼',
  famine: '◌',
  strike: '✋',
  lockdown: '⛨',
  blockade: '⊘',
  war: '⚔',
  isolation: '◎',
}
export const STATE_LABELS: Readonly<Record<StateKind, string>> = {
  boom: 'Boom',
  bust: 'Bust',
  famine: 'Famine',
  strike: 'Strike',
  lockdown: 'Lockdown',
  blockade: 'Blockade',
  war: 'War',
  isolation: 'Isolation',
}

// ---------------------------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------------------------

export interface BoardSystemInfo {
  id: string
  name: string
  region: RegionId
}

/**
 * A system's per-faction detail (a `ClaimView`'s scores and flip, and the corporation block when it holds the
 * system; from the system view or a save decode).
 */
export type ClaimDetail = Pick<ClaimView, 'scores' | 'flip'> & { corp?: ClaimCorpView | null }

export interface BoardInput {
  systems: readonly BoardSystemInfo[]
  galaxy: GalaxyEconomyView | null
  claims: ReadonlyMap<string, ClaimDetail>
  relations: RelationsView | null
  gate: GateView | null
  votes: readonly CharterVote[]
  /** The current faction day (vote countdowns). */
  day: number
  /** The player corporation once founded (its name and colour), else null or absent. */
  corp?: BoardCorp | null
}

export type BoardSort = 'name' | 'region' | 'holder' | 'lead' | 'flip'
export const BOARD_SORTS: readonly BoardSort[] = ['name', 'region', 'holder', 'lead', 'flip']
export const SORT_LABELS: Readonly<Record<BoardSort, string>> = {
  name: 'Name',
  region: 'Region',
  holder: 'Holder',
  lead: 'Lead',
  flip: 'Flip soonest',
}

export interface BoardFilter {
  region: RegionId | 'all'
  faction: FactionId | 'all'
  contested: boolean
  atWar: boolean
}
export const DEFAULT_FILTER: Readonly<BoardFilter> = Object.freeze({
  region: 'all',
  faction: 'all',
  contested: false,
  atWar: false,
})

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

export interface Sparkline {
  faction: CorpFactionId
  look: FactionLook
  /** SVG polyline points (`x,y x,y …`), empty with fewer than one value. */
  points: string
  last: number | null
}

export interface ComponentBars {
  faction: CorpFactionId
  look: FactionLook
  ops: number
  sec: number
  inf: number
  s: number
}

export interface BoardRow {
  id: string
  name: string
  region: RegionId
  regionLabel: string
  holder: CorpFactionId | null
  holderLook: FactionLook | null
  holderS: number | null
  challenger: CorpFactionId | null
  challengerLook: FactionLook | null
  challengerS: number | null
  /** Holder S − challenger S (or the top two's gap when unclaimed); null without scores. */
  lead: number | null
  sparks: Sparkline[]
  states: {
    kind: StateKind
    stage: 'pending' | 'active' | 'recovering'
    glyph: string
    label: string
  }[]
  tariff: number
  /** The tariff cell: "6%", "—", or for a corporation claim its manual tariff or "not set". */
  tariffText: string
  toll: number
  contested: boolean
  atWar: boolean
  /** Days left in a challenger's streak before the claim flips (null = no flip under way). */
  flip: { challenger: CorpFactionId; look: FactionLook; daysLeft: number } | null
  /** A corporation-held row: its name, security band ("Medium"; null when unknown) and tariff cell. */
  corp: { name: string; band: string | null; tariff: string } | null
  components: ComponentBars[]
}

/**
 * Polyline points for `values` in a `width × height` box over the S domain `0..max(sMax, values)`: x spreads the
 * values evenly (one value sits at the right edge), y is 0 at the top. Non-finite values are skipped.
 */
export function sparklinePoints(values: readonly number[], width: number, height: number, sMax: number): string {
  const vals = values.filter((v) => Number.isFinite(v))
  if (vals.length === 0) return ''
  let hi = sMax
  for (const v of vals) if (v > hi) hi = v
  const n = vals.length
  const out: string[] = []
  for (let i = 0; i < n; i++) {
    const x = n === 1 ? width : (i / (n - 1)) * width
    const v = Math.max(0, vals[i])
    const y = height - (v / hi) * height
    out.push(`${round1(x)},${round1(y)}`)
  }
  return out.join(' ')
}

function round1(v: number): number {
  return Math.round(v * 10) / 10
}

/** The factions at war with anyone (from the relations pairs). */
export function factionsAtWar(relations: RelationsView | null): ReadonlySet<FactionId> {
  const set = new Set<FactionId>()
  if (relations === null) return set
  for (const p of relations.pairs) {
    if (p.relation !== 'war' || p.a === PLAYER_FACTION_ID || p.b === PLAYER_FACTION_ID) continue
    set.add(p.a)
    set.add(p.b)
  }
  return set
}

/** The war pairs, each once. */
export function activeWars(relations: RelationsView | null): { a: FactionId; b: FactionId }[] {
  if (relations === null) return []
  const out: { a: FactionId; b: FactionId }[] = []
  for (const p of relations.pairs) if (p.relation === 'war' && p.a !== PLAYER_FACTION_ID && p.b !== PLAYER_FACTION_ID) out.push({ a: p.a, b: p.b })
  return out
}

/** Systems at a war front (an end of a front relay, or the holder is at war). */
function frontSystems(galaxy: GalaxyEconomyView | null, relayEnds: ReadonlyMap<string, readonly [string, string]>): ReadonlySet<string> {
  const set = new Set<string>()
  for (const f of galaxy?.fronts ?? []) {
    const ends = relayEnds.get(f.relayId)
    if (ends !== undefined) {
      set.add(ends[0])
      set.add(ends[1])
    }
  }
  return set
}

export interface BoardRowOptions {
  sparkline: BoardConfig['sparkline']
  /** Relay id → its two system ids (to place the war fronts). */
  relayEnds?: ReadonlyMap<string, readonly [string, string]>
}

/** Every system's row, in the systems' order (unsorted, unfiltered). */
export function boardRows(input: BoardInput, opts: BoardRowOptions): BoardRow[] {
  const summaries = new Map<string, NonNullable<GalaxyEconomyView['systems'][number]['charter']>>()
  for (const s of input.galaxy?.systems ?? []) if (s.charter !== undefined) summaries.set(s.id, s.charter)
  const warring = factionsAtWar(input.relations)
  const fronts = frontSystems(input.galaxy, opts.relayEnds ?? new Map())
  const { width, height, sMax } = opts.sparkline
  const rows: BoardRow[] = []
  for (const sys of input.systems) {
    const sum = summaries.get(sys.id) ?? null
    const detail = input.claims.get(sys.id) ?? null
    const holder = sum?.holder ?? null
    const scores = detail !== null ? [...detail.scores].sort((a, b) => b.s - a.s) : []
    const holderScore = holder !== null ? (scores.find((r) => r.faction === holder) ?? null) : null
    const top = holderScore ?? scores[0] ?? null
    const challengerScore = scores.find((r) => r !== top) ?? null
    const holderS = holderScore?.s ?? null
    const challengerS = challengerScore?.s ?? null
    const lead = top !== null ? top.s - (challengerS ?? 0) : null
    const top2 = [top, challengerScore].filter((r): r is NonNullable<typeof r> => r !== null)
    const rawFlip = detail?.flip ?? null
    const flip = rawFlip
    const corpDetail = detail?.corp ?? null
    const tariff = sum?.tariff ?? 0
    const corpRow =
      holder === PLAYER_FACTION_ID
        ? {
            name: corpClaimName(input.corp ?? corpDetail),
            band: corpDetail !== null ? bandLabel(corpDetail.band) : null,
            tariff: corpDetail !== null ? corpTariffCell(corpDetail.tariffSet, tariff) : tariffLabel(tariff),
          }
        : null
    rows.push({
      id: sys.id,
      name: sys.name,
      region: sys.region,
      regionLabel: REGION_LABELS[sys.region],
      holder,
      holderLook: holder !== null ? holderLook(holder, input.corp) : null,
      holderS,
      challenger: challengerScore?.faction ?? null,
      challengerLook: challengerScore !== null ? holderLook(challengerScore.faction, input.corp) : null,
      challengerS,
      lead,
      sparks: top2.map((r) => ({
        faction: r.faction,
        look: holderLook(r.faction, input.corp),
        points: sparklinePoints(r.trend, width, height, sMax),
        last: r.trend.length > 0 ? r.trend[r.trend.length - 1] : null,
      })),
      states: (sum?.states ?? []).map((st) => ({
        kind: st.kind,
        stage: st.stage,
        glyph: STATE_GLYPHS[st.kind],
        label: STATE_LABELS[st.kind],
      })),
      tariff,
      tariffText: corpRow !== null ? corpRow.tariff : tariffLabel(tariff),
      toll: sum?.toll ?? 0,
      contested: sum?.contested ?? false,
      atWar:
        fronts.has(sys.id) ||
        (holder !== null && holder !== PLAYER_FACTION_ID && warring.has(holder)) || (sum?.states.some((st) => st.kind === 'war' && st.stage !== 'recovering') ?? false),
      flip:
        flip !== null
          ? {
              challenger: flip.challenger,
              look: holderLook(flip.challenger, input.corp),
              daysLeft: flip.daysLeft,
            }
          : null,
      corp: corpRow,
      components: scores.map((r) => ({
        faction: r.faction,
        look: holderLook(r.faction, input.corp),
        ops: r.ops,
        sec: r.sec,
        inf: r.inf,
        s: r.s,
      })),
    })
  }
  return rows
}

/** Rows that pass `filter` (a faction filter matches the holder or the challenger). */
export function filterRows(rows: readonly BoardRow[], filter: BoardFilter): BoardRow[] {
  return rows.filter(
    (r) =>
      (filter.region === 'all' || r.region === filter.region) &&
      (filter.faction === 'all' || r.holder === filter.faction || r.challenger === filter.faction) &&
      (!filter.contested || r.contested) &&
      (!filter.atWar || r.atWar),
  )
}

const byName = (a: BoardRow, b: BoardRow): number => a.name.localeCompare(b.name)

/** A sorted copy: name A→Z; region (gate → rim); holder name (unclaimed last); smallest lead first; soonest flip first. */
export function sortRows(rows: readonly BoardRow[], sort: BoardSort): BoardRow[] {
  const out = [...rows]
  switch (sort) {
    case 'name':
      return out.sort(byName)
    case 'region':
      return out.sort((a, b) => REGION_ORDER.indexOf(a.region) - REGION_ORDER.indexOf(b.region) || byName(a, b))
    case 'holder':
      return out.sort(
        (a, b) =>
          (a.holderLook === null ? 1 : 0) - (b.holderLook === null ? 1 : 0) ||
          (a.holderLook?.name ?? '').localeCompare(b.holderLook?.name ?? '') ||
          byName(a, b),
      )
    case 'lead':
      return out.sort((a, b) => (a.lead ?? Infinity) - (b.lead ?? Infinity) || byName(a, b))
    case 'flip':
      return out.sort((a, b) => (a.flip?.daysLeft ?? Infinity) - (b.flip?.daysLeft ?? Infinity) || byName(a, b))
  }
}

/** Steps a cycle of options by `dir` (wrapping). */
export function stepOption<T>(options: readonly T[], current: T, dir: 1 | -1): T {
  const i = options.indexOf(current)
  const n = options.length
  return options[((((i < 0 ? 0 : i) + dir) % n) + n) % n]
}

// ---------------------------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------------------------

export interface ClaimShare {
  faction: CorpFactionId
  look: FactionLook
  count: number
  /** count / all systems (0..1): the stacked bar's segment width. */
  fraction: number
}

export interface BoardHeader {
  claims: ClaimShare[]
  unclaimed: number
  total: number
  wars: { a: FactionId; b: FactionId; label: string }[]
  /** "Gate operating" … "Closed"; from story tier 3 with the stability number. */
  gate: string
  isolation: boolean
  pendingVote: string | null
}

/** Claims per faction (most first), active wars, the gate status text and the next pending vote. */
export function boardHeader(input: BoardInput): BoardHeader {
  const counts = new Map<CorpFactionId, number>()
  let unclaimed = 0
  let total = 0
  for (const s of input.galaxy?.systems ?? []) {
    const c = s.charter
    if (c === undefined) continue
    total++
    const h = c.holder
    if (h === null) unclaimed++
    else counts.set(h, (counts.get(h) ?? 0) + 1)
  }
  const claims = [...counts.entries()]
    .map(([faction, count]) => ({
      faction,
      look: holderLook(faction, input.corp),
      count,
      fraction: total > 0 ? count / total : 0,
    }))
    .sort((a, b) => b.count - a.count || a.look.name.localeCompare(b.look.name))
  const wars = activeWars(input.relations).map((w) => ({
    ...w,
    label: `${factionLook(w.a).name} ⚔ ${factionLook(w.b).name}`,
  }))
  const g = input.gate
  let gate = g === null ? 'Gate status unknown' : gateStatusText(g.stage)
  if (g !== null && g.stability !== null) gate = `${gate} · resonance ${Math.round(g.stability)}`
  const pending = input.votes.find((v) => v.result === 'pending') ?? null
  return {
    claims,
    unclaimed,
    total,
    wars,
    gate,
    isolation: g?.isolation ?? false,
    pendingVote: pending !== null ? `${MEASURE_LABELS[pending.measure]}: vote ${dayCountdown(pending.voteDay, input.day)}` : null,
  }
}

// ---------------------------------------------------------------------------------------------
// Votes
// ---------------------------------------------------------------------------------------------

export const MEASURE_LABELS: Readonly<Record<CharterVote['measure'], string>> = {
  tetherstoneBan: 'Tetherstone ban',
  tetherstoneRepeal: 'Tetherstone ban repeal',
}

/** "in 2 days", "tomorrow", "today", "2 days ago". */
export function dayCountdown(targetDay: number, day: number): string {
  const d = targetDay - day
  if (d === 0) return 'today'
  if (d === 1) return 'tomorrow'
  if (d > 1) return `in ${d} days`
  return d === -1 ? 'yesterday' : `${-d} days ago`
}

export interface VoteRow {
  id: string
  measure: string
  sponsor: string
  /** "Binds ‹Corp› space: …" for a ban measure once the corporation exists (spec §27.8), else null. */
  scope: string | null
  when: string
  evidence: string
  result: 'pending' | 'passed' | 'failed'
  resultLabel: string
  ballots: {
    faction: CorpFactionId
    look: FactionLook
    weight: number
    yes: boolean
    label: string
  }[]
  yes: number
  no: number
}

const RESULT_LABELS: Readonly<Record<VoteRow['result'], string>> = {
  pending: '… Pending',
  passed: '✔ Passed',
  failed: '✖ Failed',
}

/** A ballot's faction: the AI id, or `'player'` for the corporation's ballot (cast from its slot, outside the 8 ids). */
export function ballotFaction(faction: string | undefined): CorpFactionId {
  const i = (FACTION_IDS as readonly (string | undefined)[]).indexOf(faction)
  return i >= 0 ? FACTION_IDS[i] : PLAYER_FACTION_ID
}

/**
 * The Votes panel: pending first, then the newest resolved. The corporation's ballot (weight = claims held) shows
 * under its name, and with the corporation founded (`corp`) a ban notes that it binds the corporation's space.
 */
export function votesModel(votes: readonly CharterVote[], day: number, corp: BoardCorp | null = null): VoteRow[] {
  const rows = votes.map((v): VoteRow => {
    let yes = 0
    let no = 0
    for (const b of v.ballots) {
      if (b.yes) yes += b.weight
      else no += b.weight
    }
    return {
      id: v.id,
      measure: MEASURE_LABELS[v.measure],
      sponsor: v.sponsor === 'player' ? 'You' : factionLook(v.sponsor).name,
      scope: corp !== null && v.measure === 'tetherstoneBan' ? corpScopeText(corp) : null,
      when: v.result === 'pending' ? `Hearing ${dayCountdown(v.voteDay, day)}` : `Voted day ${v.voteDay}`,
      evidence: `Evidence ${Math.round(v.evidenceWeight * 100)}%`,
      result: v.result,
      resultLabel: RESULT_LABELS[v.result],
      ballots: v.ballots.map((b) => {
        const faction = ballotFaction(b.faction)
        const look = holderLook(faction, corp)
        return { faction, look, weight: b.weight, yes: b.yes, label: `${look.name}: ${b.yes ? 'Yes' : 'No'} (${fmtWeight(b.weight)})` }
      }),
      yes,
      no,
    }
  })
  const rank = (r: VoteRow): number => (r.result === 'pending' ? 0 : 1)
  return rows.sort((a, b) => rank(a) - rank(b) || b.id.localeCompare(a.id, undefined, { numeric: true }))
}

function fmtWeight(w: number): string {
  return Number.isInteger(w) ? String(w) : w.toFixed(1)
}

/** "6%" (the tariff is a fraction, claims.json `tariff.max` 0.1); "—" for none. */
export function tariffLabel(tariff: number): string {
  if (!(tariff > 0)) return '—'
  return `${Math.round(tariff * 100)}%`
}

/** "150 cr" or "—". */
export function tollLabel(toll: number): string {
  return toll > 0 ? `${Math.round(toll)} cr` : '—'
}
